import {
  CAPITAL_GAINS_RATE,
  DARF_MINIMUM,
  STOCK_MONTHLY_EXEMPTION,
  VARIABLE_INCOME_TYPES,
  earningKinds,
} from '../types';
import type {
  Conversion,
  CorporateAction,
  Earning,
  InvestmentAsset,
  InvestmentType,
  Trade,
} from '../types';

/**
 * Apuração de carteira de renda variável (ações e FII), seguindo as regras
 * da Receita Federal para pessoa física em swing trade:
 *
 * - Custo de aquisição pelo CUSTO MÉDIO PONDERADO, taxas incluídas.
 * - Venda NÃO altera o custo médio, apenas a quantidade.
 * - Amortização de FII e restituição de capital REDUZEM o custo médio
 *   (não são rendimento).
 * - Prejuízos são compensáveis apenas dentro do mesmo bucket (ações x FII)
 *   e prejuízo apurado em mês isento não é compensável.
 */

// ============================================
// TIMELINE
// ============================================

type LedgerEventKind =
  | 'buy'
  | 'bonus'
  | 'split'
  | 'convertOut'
  | 'convertIn'
  | 'costReduction'
  | 'sell';

/** Ordem de aplicação dentro de uma mesma data. */
const EVENT_ORDER: Record<LedgerEventKind, number> = {
  buy: 0,
  bonus: 1,
  split: 2,
  // A saída precisa rodar antes da entrada para o custo viajar junto.
  convertOut: 3,
  convertIn: 4,
  costReduction: 5,
  sell: 6,
};

interface LedgerEvent {
  kind: LedgerEventKind;
  date: Date;
  assetId: string;
  /** buy/sell/bonus: quantidade. split: fator. */
  quantity: number;
  /** Valor em R$ que entra (buy/bonus) ou sai (costReduction) do custo. */
  cost: number;
  /** Venda: receita líquida de taxas. */
  proceeds: number;
  /** Venda: valor bruto (base da isenção mensal de R$ 20k). */
  grossProceeds: number;
  irrfWithheld: number;
  sourceId: string;
}

function tradeToEvent(trade: Trade): LedgerEvent {
  const gross = trade.quantity * trade.unitPrice;
  if (trade.operation === 'buy') {
    return {
      kind: 'buy',
      date: trade.date,
      assetId: trade.assetId,
      quantity: trade.quantity,
      cost: gross + trade.fees,
      proceeds: 0,
      grossProceeds: 0,
      irrfWithheld: 0,
      sourceId: trade.id,
    };
  }
  return {
    kind: 'sell',
    date: trade.date,
    assetId: trade.assetId,
    quantity: trade.quantity,
    cost: 0,
    proceeds: gross - trade.fees,
    grossProceeds: gross,
    irrfWithheld: trade.irrfWithheld ?? 0,
    sourceId: trade.id,
  };
}

function earningToEvent(earning: Earning): LedgerEvent | null {
  const treatment = earningKinds[earning.kind]?.treatment;

  // Amortização / restituição de capital: devolução de principal, abate custo.
  if (treatment === 'custo') {
    return {
      kind: 'costReduction',
      date: earning.paymentDate,
      assetId: earning.assetId,
      quantity: 0,
      cost: earning.grossValue,
      proceeds: 0,
      grossProceeds: 0,
      irrfWithheld: 0,
      sourceId: earning.id,
    };
  }

  // Bonificação: entram ações novas pelo custo informado pela empresa
  // (grossValue = 0 significa bonificação sem custo, que dilui o custo médio).
  if (treatment === 'quantidade') {
    return {
      kind: 'bonus',
      date: earning.paymentDate,
      assetId: earning.assetId,
      quantity: earning.quantityBase ?? 0,
      cost: earning.grossValue,
      proceeds: 0,
      grossProceeds: 0,
      irrfWithheld: 0,
      sourceId: earning.id,
    };
  }

  // Dividendo / JCP / rendimento de FII não afetam a posição.
  return null;
}

function conversionToEvents(conversion: Conversion): LedgerEvent[] {
  const base = {
    date: conversion.date,
    cost: 0,
    proceeds: 0,
    grossProceeds: 0,
    irrfWithheld: 0,
    sourceId: conversion.id,
  };
  return [
    { ...base, kind: 'convertOut', assetId: conversion.fromAssetId, quantity: conversion.fromQuantity },
    { ...base, kind: 'convertIn', assetId: conversion.toAssetId, quantity: conversion.toQuantity },
  ];
}

function corporateActionToEvent(action: CorporateAction): LedgerEvent {
  return {
    kind: 'split',
    date: action.date,
    assetId: action.assetId,
    quantity: action.factor,
    cost: 0,
    proceeds: 0,
    grossProceeds: 0,
    irrfWithheld: 0,
    sourceId: action.id,
  };
}

function buildTimeline(
  trades: Trade[],
  earnings: Earning[],
  corporateActions: CorporateAction[],
  conversions: Conversion[] = []
): LedgerEvent[] {
  const events: LedgerEvent[] = [
    ...trades.map(tradeToEvent),
    ...earnings.map(earningToEvent).filter((e): e is LedgerEvent => e !== null),
    ...corporateActions.map(corporateActionToEvent),
    ...conversions.flatMap(conversionToEvents),
  ];

  return events.sort((a, b) => {
    const dateDiff = a.date.getTime() - b.date.getTime();
    if (dateDiff !== 0) return dateDiff;
    return EVENT_ORDER[a.kind] - EVENT_ORDER[b.kind];
  });
}

// ============================================
// POSIÇÃO E GANHOS REALIZADOS
// ============================================

interface LedgerState {
  quantity: number;
  totalCost: number;
}

export interface RealizedSale {
  tradeId: string;
  assetId: string;
  type: InvestmentType;
  date: Date;
  quantity: number;
  /** Valor bruto da venda — base da isenção mensal. */
  grossProceeds: number;
  /** Valor da venda líquido de taxas. */
  netProceeds: number;
  /** Custo médio x quantidade vendida. */
  costBasis: number;
  /** netProceeds - costBasis. Negativo = prejuízo. */
  result: number;
  irrfWithheld: number;
}

export interface Position {
  asset: InvestmentAsset;
  quantity: number;
  /** Custo médio por cota/ação. */
  averageCost: number;
  /** Custo total de aquisição da posição atual — é o valor do Bens e Direitos. */
  totalCost: number;
  currentPrice: number | null;
  marketValue: number | null;
  unrealizedGain: number | null;
  unrealizedGainPct: number | null;
}

/** Estado de todas as posições após aplicar os eventos até `cutoff` (inclusive). */
function replay(
  events: LedgerEvent[],
  cutoff?: Date
): { states: Map<string, LedgerState>; sales: LedgerEvent[]; salesCost: Map<string, number> } {
  const states = new Map<string, LedgerState>();
  const sales: LedgerEvent[] = [];
  const salesCost = new Map<string, number>();
  /** Custo em trânsito entre os dois lados de uma conversão. */
  const inFlight = new Map<string, number>();

  for (const event of events) {
    if (cutoff && event.date.getTime() > cutoff.getTime()) break;

    const state = states.get(event.assetId) ?? { quantity: 0, totalCost: 0 };

    switch (event.kind) {
      case 'buy':
      case 'bonus':
        state.quantity += event.quantity;
        state.totalCost += event.cost;
        break;

      case 'split':
        // Desdobramento/grupamento: muda a quantidade, mantém o custo total.
        if (event.quantity > 0) state.quantity *= event.quantity;
        break;

      case 'convertOut': {
        const averageCost = state.quantity > 0 ? state.totalCost / state.quantity : 0;
        const leaving = Math.min(event.quantity, state.quantity);
        const cost = averageCost * leaving;
        inFlight.set(event.sourceId, cost);
        state.quantity -= leaving;
        state.totalCost -= cost;
        if (Math.abs(state.quantity) < 1e-8) {
          state.quantity = 0;
          state.totalCost = 0;
        }
        break;
      }

      case 'convertIn':
        state.quantity += event.quantity;
        state.totalCost += inFlight.get(event.sourceId) ?? 0;
        break;

      case 'costReduction':
        // Nunca deixa o custo negativo: o excedente seria ganho de capital,
        // caso raro que o usuário deve lançar como venda.
        state.totalCost = Math.max(0, state.totalCost - event.cost);
        break;

      case 'sell': {
        const averageCost = state.quantity > 0 ? state.totalCost / state.quantity : 0;
        const costBasis = averageCost * event.quantity;
        salesCost.set(event.sourceId, costBasis);
        sales.push(event);
        state.quantity -= event.quantity;
        state.totalCost -= costBasis;
        // Zera resíduos de ponto flutuante quando a posição é encerrada.
        if (Math.abs(state.quantity) < 1e-8) {
          state.quantity = 0;
          state.totalCost = 0;
        }
        break;
      }
    }

    states.set(event.assetId, state);
  }

  return { states, sales, salesCost };
}

export interface PortfolioInput {
  assets: InvestmentAsset[];
  trades: Trade[];
  earnings: Earning[];
  corporateActions?: CorporateAction[];
  conversions?: Conversion[];
}

/** Posições na data informada (default: hoje), uma por ativo com quantidade > 0. */
export function computePositions(
  { assets, trades, earnings, corporateActions = [], conversions = [] }: PortfolioInput,
  cutoff?: Date
): Position[] {
  const events = buildTimeline(trades, earnings, corporateActions, conversions);
  const { states } = replay(events, cutoff);

  return assets
    .map((asset) => {
      const state = states.get(asset.id) ?? { quantity: 0, totalCost: 0 };
      const quantity = state.quantity;
      const totalCost = state.totalCost;
      const averageCost = quantity > 0 ? totalCost / quantity : 0;
      const currentPrice = asset.currentPrice ?? null;
      const marketValue = currentPrice !== null ? currentPrice * quantity : null;
      const unrealizedGain = marketValue !== null ? marketValue - totalCost : null;

      return {
        asset,
        quantity,
        averageCost,
        totalCost,
        currentPrice,
        marketValue,
        unrealizedGain,
        unrealizedGainPct:
          unrealizedGain !== null && totalCost > 0
            ? (unrealizedGain / totalCost) * 100
            : null,
      };
    })
    .filter((p) => p.quantity > 0);
}

/** Todas as vendas com custo médio e resultado apurado, em ordem cronológica. */
export function computeRealizedSales({
  assets,
  trades,
  earnings,
  corporateActions = [],
  conversions = [],
}: PortfolioInput): RealizedSale[] {
  const assetById = new Map(assets.map((a) => [a.id, a]));
  const events = buildTimeline(trades, earnings, corporateActions, conversions);
  const { sales, salesCost } = replay(events);

  return sales.flatMap((sale) => {
    const asset = assetById.get(sale.assetId);
    if (!asset) return [];
    const costBasis = salesCost.get(sale.sourceId) ?? 0;
    return [
      {
        tradeId: sale.sourceId,
        assetId: sale.assetId,
        type: asset.type,
        date: sale.date,
        quantity: sale.quantity,
        grossProceeds: sale.grossProceeds,
        netProceeds: sale.proceeds,
        costBasis,
        result: sale.proceeds - costBasis,
        irrfWithheld: sale.irrfWithheld,
      },
    ];
  });
}

/**
 * Quantidade disponível de um ativo numa data — usado para impedir que o
 * usuário registre venda maior que a posição.
 */
export function availableQuantityAt(
  input: PortfolioInput,
  assetId: string,
  date: Date,
  ignoreTradeId?: string
): number {
  const trades = ignoreTradeId
    ? input.trades.filter((t) => t.id !== ignoreTradeId)
    : input.trades;
  const events = buildTimeline(
    trades,
    input.earnings,
    input.corporateActions ?? [],
    input.conversions ?? []
  );
  const { states } = replay(events, date);
  return states.get(assetId)?.quantity ?? 0;
}

// ============================================
// APURAÇÃO MENSAL DE RENDA VARIÁVEL
// ============================================

export interface MonthlyTaxBucket {
  type: InvestmentType;
  /** Soma bruta das vendas no mês. */
  sales: number;
  /** Resultado do mês (lucros - prejuízos das vendas). */
  result: number;
  /** Mês isento por ficar no limite de R$ 20k (só ações). */
  exempt: boolean;
  /** Prejuízo acumulado disponível no início do mês. */
  lossCarryIn: number;
  /** Prejuízo efetivamente compensado neste mês. */
  lossUsed: number;
  /** Base de cálculo após compensação. */
  taxableBase: number;
  /** Prejuízo acumulado no fim do mês. */
  lossCarryOut: number;
  tax: number;
}

export interface MonthlyTax {
  year: number;
  /** 1-12. */
  month: number;
  buckets: MonthlyTaxBucket[];
  /** Soma dos impostos dos buckets. */
  taxDue: number;
  irrfWithheld: number;
  /** DARF acumulada de meses anteriores que ficaram abaixo de R$ 10. */
  carriedFromPreviousMonths: number;
  /** Valor a recolher no mês (0 quando acumula para o mês seguinte). */
  darfToPay: number;
  /** Saldo que segue acumulando por não atingir o mínimo. */
  darfCarriedForward: number;
  /** Prejuízo apurado em mês isento, que a legislação não permite compensar. */
  nonDeductibleLoss: number;
}

/**
 * Buckets da apuração mensal. Tesouro Direto não entra: o IR é retido na
 * fonte pela corretora, então não há DARF nem compensação de prejuízo.
 */
const BUCKETS: InvestmentType[] = VARIABLE_INCOME_TYPES;

/**
 * Apura mês a mês todo o histórico, para que o prejuízo acumulado de anos
 * anteriores entre corretamente no ano consultado.
 */
export function computeMonthlyTaxes(input: PortfolioInput): MonthlyTax[] {
  const sales = computeRealizedSales(input);
  if (sales.length === 0) return [];

  const byMonth = new Map<string, RealizedSale[]>();
  for (const sale of sales) {
    const key = `${sale.date.getFullYear()}-${sale.date.getMonth() + 1}`;
    const list = byMonth.get(key) ?? [];
    list.push(sale);
    byMonth.set(key, list);
  }

  const first = sales[0]!.date;
  const last = sales[sales.length - 1]!.date;

  const lossCarry: Record<InvestmentType, number> = { acao: 0, fii: 0, tesouro: 0 };
  let darfCarry = 0;
  const months: MonthlyTax[] = [];

  const cursor = new Date(first.getFullYear(), first.getMonth(), 1);
  const end = new Date(last.getFullYear(), last.getMonth(), 1);

  while (cursor.getTime() <= end.getTime()) {
    const year = cursor.getFullYear();
    const month = cursor.getMonth() + 1;
    const monthSales = byMonth.get(`${year}-${month}`) ?? [];

    const buckets: MonthlyTaxBucket[] = [];
    let nonDeductibleLoss = 0;

    for (const type of BUCKETS) {
      const bucketSales = monthSales.filter((s) => s.type === type);
      const salesTotal = bucketSales.reduce((sum, s) => sum + s.grossProceeds, 0);
      const result = bucketSales.reduce((sum, s) => sum + s.result, 0);

      // Isenção de R$ 20 mil/mês vale só para ações em swing trade.
      const exempt = type === 'acao' && salesTotal > 0 && salesTotal <= STOCK_MONTHLY_EXEMPTION;

      const lossCarryIn = lossCarry[type];
      let lossUsed = 0;
      let taxableBase = 0;

      if (exempt) {
        // Lucro isento; prejuízo em mês isento não entra no estoque compensável.
        if (result < 0) nonDeductibleLoss += Math.abs(result);
      } else if (result > 0) {
        lossUsed = Math.min(lossCarryIn, result);
        taxableBase = result - lossUsed;
        lossCarry[type] = lossCarryIn - lossUsed;
      } else if (result < 0) {
        lossCarry[type] = lossCarryIn + Math.abs(result);
      }

      const tax = taxableBase * CAPITAL_GAINS_RATE[type];

      buckets.push({
        type,
        sales: salesTotal,
        result,
        exempt,
        lossCarryIn,
        lossUsed,
        taxableBase,
        lossCarryOut: lossCarry[type],
        tax,
      });
    }

    const taxDue = buckets.reduce((sum, b) => sum + b.tax, 0);
    const irrfWithheld = monthSales.reduce((sum, s) => sum + s.irrfWithheld, 0);
    const carriedFromPreviousMonths = darfCarry;

    // IRRF retido na venda é compensado com o imposto apurado.
    const netTax = Math.max(0, taxDue - irrfWithheld) + carriedFromPreviousMonths;
    const reachedMinimum = netTax >= DARF_MINIMUM;
    const darfToPay = reachedMinimum ? netTax : 0;
    darfCarry = reachedMinimum ? 0 : netTax;

    const hasActivity = monthSales.length > 0 || darfToPay > 0;

    if (hasActivity) {
      months.push({
        year,
        month,
        buckets,
        taxDue,
        irrfWithheld,
        carriedFromPreviousMonths,
        darfToPay,
        darfCarriedForward: darfCarry,
        nonDeductibleLoss,
      });
    }

    cursor.setMonth(cursor.getMonth() + 1);
  }

  return months;
}

// ============================================
// TABELAS DO IRPF
// ============================================

export interface BensEDireitosRow {
  asset: InvestmentAsset;
  grupo: string;
  codigo: string;
  cnpj: string;
  quantityCurrent: number;
  quantityPrevious: number;
  /** Custo de aquisição em 31/12 do ano-base. */
  costCurrent: number;
  /** Custo de aquisição em 31/12 do ano anterior. */
  costPrevious: number;
  averageCostCurrent: number;
  discriminacao: string;
}

function endOfYear(year: number): Date {
  return new Date(year, 11, 31, 23, 59, 59, 999);
}

function formatQuantity(value: number): string {
  return Number.isInteger(value)
    ? String(value)
    : value.toLocaleString('pt-BR', { maximumFractionDigits: 8 });
}

function formatBrl(value: number): string {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Ficha Bens e Direitos: posição em 31/12 pelo CUSTO de aquisição
 * (nunca pelo valor de mercado), do ano-base e do ano anterior.
 */
export function buildBensEDireitos(
  input: PortfolioInput,
  year: number,
  codes: {
    bensAcao: { grupo: string; codigo: string };
    bensFii: { grupo: string; codigo: string };
    bensTesouro: { grupo: string; codigo: string };
  }
): BensEDireitosRow[] {
  const current = computePositions(input, endOfYear(year));
  const previous = computePositions(input, endOfYear(year - 1));

  const previousById = new Map(previous.map((p) => [p.asset.id, p]));
  const currentById = new Map(current.map((p) => [p.asset.id, p]));
  const allIds = new Set([...currentById.keys(), ...previousById.keys()]);

  return [...allIds]
    .flatMap((id) => {
      const position = currentById.get(id);
      const previousPosition = previousById.get(id);
      const asset = position?.asset ?? previousPosition?.asset;
      if (!asset) return [];

      const code =
        asset.type === 'acao'
          ? codes.bensAcao
          : asset.type === 'fii'
            ? codes.bensFii
            : codes.bensTesouro;
      const unit =
        asset.type === 'acao' ? 'ações' : asset.type === 'fii' ? 'cotas' : 'títulos';
      const cnpj = asset.cnpj ?? '';
      const quantityCurrent = position?.quantity ?? 0;
      const costCurrent = position?.totalCost ?? 0;
      const averageCostCurrent = position?.averageCost ?? 0;

      const identification =
        asset.type === 'tesouro' ? asset.name : `${asset.ticker} - ${asset.name}`;

      const discriminacao =
        `${formatQuantity(quantityCurrent)} ${unit} de ${identification}` +
        (cnpj ? `, CNPJ ${cnpj}` : '') +
        `, custo médio de aquisição R$ ${formatBrl(averageCostCurrent)} por ${
          asset.type === 'acao' ? 'ação' : asset.type === 'fii' ? 'cota' : 'título'
        }` +
        `, custo total R$ ${formatBrl(costCurrent)}.`;

      return [
        {
          asset,
          grupo: code.grupo,
          codigo: code.codigo,
          cnpj,
          quantityCurrent,
          quantityPrevious: previousPosition?.quantity ?? 0,
          costCurrent,
          costPrevious: previousPosition?.totalCost ?? 0,
          averageCostCurrent,
          discriminacao,
        },
      ];
    })
    .sort((a, b) => a.asset.ticker.localeCompare(b.asset.ticker));
}

export interface EarningsRow {
  asset: InvestmentAsset;
  cnpj: string;
  codigo: string;
  label: string;
  grossValue: number;
  irrfWithheld: number;
  netValue: number;
}

export interface EarningsTables {
  /** Rendimentos Isentos e Não Tributáveis — dividendos de ações. */
  dividendos: EarningsRow[];
  /** Rendimentos Isentos e Não Tributáveis — rendimentos de FII. */
  rendimentosFii: EarningsRow[];
  /** Tributação Exclusiva na Fonte — JCP. */
  jcp: EarningsRow[];
  /** Amortizações e restituições: não declaradas como rendimento, só reduzem custo. */
  reducoesDeCusto: EarningsRow[];
  totals: {
    dividendos: number;
    rendimentosFii: number;
    jcpGross: number;
    jcpIrrf: number;
    jcpNet: number;
    reducoesDeCusto: number;
    /** Tudo que entrou no bolso no ano. */
    received: number;
  };
}

/** Agrupa os proventos do ano-base por ficha da declaração e por fonte pagadora. */
export function buildEarningsTables(
  { assets, earnings }: Pick<PortfolioInput, 'assets' | 'earnings'>,
  year: number,
  codes: {
    isentoDividendo: { codigo: string; label: string };
    isentoFii: { codigo: string; label: string };
    exclusivaJcp: { codigo: string; label: string };
  }
): EarningsTables {
  const assetById = new Map(assets.map((a) => [a.id, a]));
  const inYear = earnings.filter((e) => e.paymentDate.getFullYear() === year);

  const group = (
    filter: (kind: Earning['kind']) => boolean,
    codigo: string,
    label: string
  ): EarningsRow[] => {
    const byAsset = new Map<string, EarningsRow>();

    for (const earning of inYear) {
      if (!filter(earning.kind)) continue;
      const asset = assetById.get(earning.assetId);
      if (!asset) continue;

      const row = byAsset.get(asset.id) ?? {
        asset,
        cnpj: asset.cnpj ?? '',
        codigo,
        label,
        grossValue: 0,
        irrfWithheld: 0,
        netValue: 0,
      };
      row.grossValue += earning.grossValue;
      row.irrfWithheld += earning.irrfWithheld;
      row.netValue = row.grossValue - row.irrfWithheld;
      byAsset.set(asset.id, row);
    }

    return [...byAsset.values()].sort((a, b) =>
      a.asset.ticker.localeCompare(b.asset.ticker)
    );
  };

  const dividendos = group(
    (k) => k === 'dividendo',
    codes.isentoDividendo.codigo,
    codes.isentoDividendo.label
  );
  const rendimentosFii = group(
    (k) => k === 'rendimento_fii',
    codes.isentoFii.codigo,
    codes.isentoFii.label
  );
  const jcp = group(
    (k) => k === 'jcp',
    codes.exclusivaJcp.codigo,
    codes.exclusivaJcp.label
  );
  const reducoesDeCusto = group(
    (k) => k === 'amortizacao' || k === 'restituicao_capital',
    '-',
    'Amortização / restituição de capital (reduz o custo de aquisição)'
  );

  const sum = (rows: EarningsRow[], key: 'grossValue' | 'irrfWithheld' | 'netValue') =>
    rows.reduce((total, row) => total + row[key], 0);

  const totalDividendos = sum(dividendos, 'grossValue');
  const totalFii = sum(rendimentosFii, 'grossValue');
  const jcpGross = sum(jcp, 'grossValue');
  const jcpIrrf = sum(jcp, 'irrfWithheld');
  const totalReducoes = sum(reducoesDeCusto, 'grossValue');

  return {
    dividendos,
    rendimentosFii,
    jcp,
    reducoesDeCusto,
    totals: {
      dividendos: totalDividendos,
      rendimentosFii: totalFii,
      jcpGross,
      jcpIrrf,
      jcpNet: jcpGross - jcpIrrf,
      reducoesDeCusto: totalReducoes,
      received:
        totalDividendos + totalFii + (jcpGross - jcpIrrf) + totalReducoes,
    },
  };
}

// ============================================
// RESUMO DA CARTEIRA
// ============================================

export interface PortfolioSummary {
  totalCost: number;
  marketValue: number;
  /** true quando algum ativo não tem preço atual preenchido. */
  partialPricing: boolean;
  unrealizedGain: number;
  unrealizedGainPct: number;
  byType: Record<InvestmentType, { cost: number; marketValue: number; share: number }>;
}

export function computeSummary(positions: Position[]): PortfolioSummary {
  const byType: PortfolioSummary['byType'] = {
    acao: { cost: 0, marketValue: 0, share: 0 },
    fii: { cost: 0, marketValue: 0, share: 0 },
    tesouro: { cost: 0, marketValue: 0, share: 0 },
  };

  let totalCost = 0;
  let marketValue = 0;
  let partialPricing = false;

  for (const position of positions) {
    const value = position.marketValue ?? position.totalCost;
    if (position.marketValue === null) partialPricing = true;
    totalCost += position.totalCost;
    marketValue += value;
    byType[position.asset.type].cost += position.totalCost;
    byType[position.asset.type].marketValue += value;
  }

  for (const type of Object.keys(byType) as InvestmentType[]) {
    byType[type].share = marketValue > 0 ? (byType[type].marketValue / marketValue) * 100 : 0;
  }

  const unrealizedGain = marketValue - totalCost;

  return {
    totalCost,
    marketValue,
    partialPricing,
    unrealizedGain,
    unrealizedGainPct: totalCost > 0 ? (unrealizedGain / totalCost) * 100 : 0,
    byType,
  };
}

/**
 * Proventos líquidos recebidos no período — o que de fato caiu na conta.
 * Amortização e restituição de capital ficam de fora: são devolução de
 * principal, não rendimento, e já abatem o custo médio.
 *
 * @param month 1-12; omitido, considera o ano inteiro.
 */
export function totalEarningsReceived(
  earnings: Earning[],
  year?: number,
  month?: number
): number {
  return earnings
    .filter((e) => {
      if (year !== undefined && e.paymentDate.getFullYear() !== year) return false;
      if (month !== undefined && e.paymentDate.getMonth() + 1 !== month) return false;
      const treatment = earningKinds[e.kind]?.treatment;
      return treatment === 'isento' || treatment === 'exclusiva';
    })
    .reduce((sum, e) => sum + (e.grossValue - e.irrfWithheld), 0);
}

/** Yield on cost: proventos do período sobre o custo da posição. */
export function yieldOnCost(earningsTotal: number, totalCost: number): number {
  return totalCost > 0 ? (earningsTotal / totalCost) * 100 : 0;
}
